import { TAB_KEY } from '@portal/enum/onboardingType';
import { AccountStatus } from '@portal/models/enumerators';
import qs from 'qs';
import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

type NavigateOptions<StateType = unknown> = {
  replace?: boolean;
  state?: StateType;
  query?: Record<string, unknown>;
};

type NavigationFunction<StateType = unknown> = {
  (to: string | number, options?: NavigateOptions<StateType>): void;
  location: {
    state: StateType | null;
    pathname: string;
  };
  back: () => void;
  getQuery: () => qs.ParsedQs;
  navigate: (routeName: string, query?: Record<string, unknown>) => void;
  reset: (routeName: string, params?: StateType) => void;
  getOnboardingTab: (status: AccountStatus) => TAB_KEY | undefined;
};

export const useNavigation = <
  StateType = unknown
>(): NavigationFunction<StateType> => {
  const history = useHistory();
  const location = useLocation<StateType>();

  const appNavigate = useCallback(
    (to: string | number, options?: NavigateOptions<StateType>) => {
      if (typeof to === 'number') {
        history.go(to);

        return;
      }
      const pathname = to;
      const search = options?.query
        ? `?${qs.stringify(options.query, { skipNulls: true })}`
        : '';

      if (options?.replace) {
        history.replace(
          {
            pathname,
            search,
          },
          options.state
        );
      } else {
        history.push(
          {
            pathname,
            search,
          },
          options?.state
        );
      }
    },
    [history]
  );

  const back = useCallback(() => {
    history.go(-1);
  }, [history]);

  const getQuery = useCallback((): qs.ParsedQs => {
    let search = location.search;
    if (search[0] === '?') {
      search = search.substring(1);
    }

    return qs.parse(search);
  }, [location.search]);

  const navigate = useCallback(
    (routeName: string, query?: Record<string, unknown>) => {
      history.push({
        pathname: routeName,
        search: query ? qs.stringify(query, { skipNulls: true }) : '',
      });
    },
    [history]
  );

  const reset = useCallback(
    (routeName: string, params?: StateType) => {
      history.push(routeName, params);
    },
    [history]
  );

  const getOnboardingTab = useCallback(
    (status: AccountStatus): TAB_KEY | undefined => {
      switch (status) {
        case AccountStatus.ERROR_TO_CREATE:
        case AccountStatus.REPROVED:
        case AccountStatus.BLOCKED:
        case AccountStatus.CANCELED:
          break;
        case AccountStatus.REQUESTED_ADDRESS:
          return TAB_KEY.ADDRESS;
        case AccountStatus.REQUESTED_SELF:
        case AccountStatus.REQUESTED_DOCUMENTS:
          return TAB_KEY.DOCUMENT;
        case AccountStatus.REQUESTED_PIN:
          return TAB_KEY.PIN;
        case AccountStatus.REQUESTED_PARTNERS:
          return TAB_KEY.NEW_REPRESENTANTS;
        case AccountStatus.REQUESTED_LEGAL_REPRESENTOR:
          return TAB_KEY.MINOR_REPRESENTANTS;
        case AccountStatus.REQUESTED_SELF_REPRESENTOR:
        case AccountStatus.REQUESTED_DOCUMENT_REPRESENTOR:
          return TAB_KEY.LEGAL_REPRESENTANTS_DOCUMENT;
        case AccountStatus.PENDING_DOCUMENTS:
        case AccountStatus.PENDING_CREATE_COREBANK_CONFIRM:
        case AccountStatus.PENDING_MANUAL_APPROVAL:
        case AccountStatus.PENDING_CREATE_COREBANK:
        case AccountStatus.PENDING_APPROVAL:
        case AccountStatus.PENDING_KYC_ANALYSIS:
        case AccountStatus.PENDING_KYC_APPROVE:
          return TAB_KEY.FINAL;
        default:
          break;
      }
    },
    []
  );

  const navigationFunction = appNavigate as NavigationFunction<StateType>;
  navigationFunction.location = {
    state: location.state ?? null,
    pathname: location.pathname,
  };
  navigationFunction.back = back;
  navigationFunction.getQuery = getQuery;
  navigationFunction.navigate = navigate;
  navigationFunction.reset = reset;
  navigationFunction.getOnboardingTab = getOnboardingTab;

  return navigationFunction;
};
